import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";

/** Start a buyer's own landing page from a template (a shared page or an existing page), then edit it. */
export function NewFromTemplateDialog({ onClose }) {
  const nav = useNavigate();
  const [buyers, setBuyers] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [buyerId, setBuyerId] = useState("");
  const [key, setKey] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  useEffect(() => {
    Promise.all([api.get("/admin/global-funnels"), api.get("/admin/lander-templates")])
      .then(([g, t]) => { setBuyers(g.data.buyers || []); setTpl(t.data); })
      .catch((e) => toast.error(errText(e, "Couldn't load buyers or templates")));
  }, []);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/buyers/${buyerId}/landers`, { templates: [key] }, notifyParams(notify));
      toast.success("Draft page made. Edit it, then publish");
      nav(`/admin/landing-pages/custom/${data.created[0].id}`);
    } catch (e) {
      toast.error(errText(e, "Couldn't make the page"));
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="tpl-new-cancel">Cancel</button>
      <button type="button" className={BTN_BLACK} onClick={create} disabled={busy || !buyerId || !key} data-testid="tpl-new-create">
        {busy ? "Making…" : "Make page and edit"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Buyer landing page" title="Start from a template" onClose={onClose} footer={footer} testId="tpl-new-dialog">
      {!buyers || !tpl ? <div className="label-mono">Loading…</div> : (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs">Buyer
            <select className={INPUT} value={buyerId} onChange={(e) => setBuyerId(e.target.value)} data-testid="tpl-new-buyer">
              <option value="">Pick a buyer</option>
              {buyers.map((b) => <option key={b.id} value={b.id}>{b.firm_name}{b.is_draft ? " (draft)" : ""}</option>)}
            </select>
          </label>
          <label className="text-xs">Template
            <select className={INPUT} value={key} onChange={(e) => setKey(e.target.value)} data-testid="tpl-new-template">
              <optgroup label="Shared pages">
                {tpl.funnels.map((f) => <option key={f.key} value={f.key}>{f.title}</option>)}
              </optgroup>
              {tpl.pages.length > 0 && (
                <optgroup label="Copy another page">
                  {tpl.pages.map((p) => <option key={p.key} value={p.key}>{p.title} (/lp/{p.slug})</option>)}
                </optgroup>
              )}
            </select>
          </label>
          <p className="text-[11px] text-zinc-600 sm:col-span-2">
            You get a draft with all the template&apos;s words, pictures and form, branded for the buyer. Leads that fit the
            buyer go to them first; if they can&apos;t take it, the next matching buyer gets it and the visitor sees that
            buyer&apos;s thank-you page.
          </p>
          <div className="sm:col-span-2"><NotifyBuyerBox value={notify} onChange={setNotify} testId="tpl-new-notify"
            preview={() => (buyerId ? { kind: "own_pages", page_id: buyerId, templates: [key] } : null)} /></div>
        </div>
      )}
    </AdminModal>
  );
}
